import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { Link, useNavigate } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { useFooterSlot } from '../layouts/footerSlotContext';
import { getNextStep, getPreviousStep, isLastStep } from '../steps';
import type { OnboardingStepId } from '../types';

export type OnboardingFooterProps = {
    currentStep: OnboardingStepId;
    nextDisabled?: boolean;
    hideNext?: boolean;
    onNext?: () => void;
    onSkip?: () => void;
    skipLabel?: string;
};

export function OnboardingFooter({
    currentStep,
    nextDisabled,
    hideNext,
    onNext,
    onSkip,
    skipLabel,
}: OnboardingFooterProps): ReactNode {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const slot = useFooterSlot();
    const previous = getPreviousStep(currentStep);
    const next = getNextStep(currentStep);
    const nextLabel = isLastStep(currentStep) ? t('ONBOARDING.FOOTER.FINISH') : t('ONBOARDING.FOOTER.NEXT');
    const resolvedSkipLabel = skipLabel ?? t('ONBOARDING.FOOTER.SKIP_FOR_NOW');

    const handleNext = (): void => {
        if (onNext) {
            onNext();
            return;
        }
        if (next) {
            void navigate({ to: next.path });
        }
    };

    if (!slot) {
        return null;
    }

    return createPortal(
        <Paper component="footer" elevation={3} square>
            <Stack
                direction="row"
                spacing={2}
                sx={{
                    alignItems: 'center',
                    justifyContent: previous ? 'space-between' : 'flex-end',
                    maxWidth: 1200,
                    mx: 'auto',
                    px: 3,
                    py: 2,
                }}>
                {previous && (
                    <Button variant="outlined" component={Link} to={previous.path}>
                        {t('ONBOARDING.FOOTER.BACK')}
                    </Button>
                )}
                <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
                    {onSkip && (
                        <Button variant="text" onClick={onSkip}>
                            {resolvedSkipLabel}
                        </Button>
                    )}
                    {!hideNext && (
                        <Button variant="contained" disabled={nextDisabled} onClick={handleNext}>
                            {nextLabel}
                        </Button>
                    )}
                </Stack>
            </Stack>
        </Paper>,
        slot,
    );
}
